import {
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnInit,
  Output,
  PLATFORM_ID,
  ViewChild
} from '@angular/core';
import {FormControl, FormGroup} from "@angular/forms";
import {TranslocoService} from "@ngneat/transloco";
import {NgbModal} from "@ng-bootstrap/ng-bootstrap";
import {AbstractComponent, ConfirmationModalComponent, SelectWaypointFromEventModalComponent} from "@components";
import {Country, EventItem, Lookups, Race, RaceRoute, RaceSettings, Waypoint, WaypointType, TOAST_STATE} from "@model";
import {LoaderService, RaceRouteService, ToastService, WaypointService} from "@services";
import {required} from "@utility";
import {environment} from "@environment";
import {isPlatformBrowser} from "@angular/common";
import {finalize} from "rxjs";

@Component({
  selector: 'app-race-waypoints',
  templateUrl: './race-waypoints.component.html',
  styleUrls: ['./race-waypoints.component.scss']
})
export class RaceWaypointsComponent extends AbstractComponent implements OnInit {
  @Input() event?: EventItem;
  @Input() race?: Race;
  @Input() disableEditing = false;
  @Input() countries: Country[] = [];
  @Input({required: true}) languages: Lookups[] = [];
  @Input({required: true}) waypointMarkerTypes: Lookups[] = [];
  @Input() waypointTypes?: Lookups[];
  @Input() waypointList: Waypoint[] = [];
  @Input() raceRoute?: RaceRoute;
  @Input() raceSettings?: RaceSettings;
  @Output() waypointListChange = new EventEmitter<Waypoint[]>();
  @Output() raceRouteChange = new EventEmitter<RaceRoute | undefined>();

  @ViewChild('fileUpload') fileUploadInputVariable?: ElementRef;

  gpx?: any;
  isTableCollapsed = false;
  isPointSelectionEnabled = false;
  currentWaypoint = new Waypoint();

  inputForm = new FormGroup({
    longitude: new FormControl(),
    latitude: new FormControl(),
    pointNameHr: new FormControl(),
    pointNameEn: new FormControl(),
    pointNameSlo: new FormControl(),
    pointNameIt: new FormControl(),
    pointNameSr: new FormControl(),
    pointNameBs: new FormControl(),
    pointSelection: new FormControl(),
    distanceFromStart: new FormControl(),
    elevation: new FormControl(),
    ordinalNumber: new FormControl(),
    waypointTypeId: new FormControl(),
    city: new FormControl(),
    address: new FormControl(),
    country: new FormControl(),
    additionalMark: new FormControl(),
    descriptionHr: new FormControl(),
    descriptionEn: new FormControl(),
    descriptionIt: new FormControl(),
    descriptionSlo: new FormControl(),
    descriptionSr: new FormControl(),
    descriptionBs: new FormControl(),
    markerTypes: new FormControl(),
    limit: new FormControl(),
    includedInRanking: new FormControl()
  });

  constructor(
    public translocoService: TranslocoService,
    private modalService: NgbModal,
    private waypointService: WaypointService,
    private loaderService: LoaderService,
    private toastService: ToastService,
    private raceRouteService: RaceRouteService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    super();
  }

  ngOnInit(): void {
    this.initForm();
    this.parseGpx();
    this.subscribeToWaypointTypeChange();
    if (this.waypointList.length > 0) {
      this.isTableCollapsed = false;
    }
  }

  private parseGpx(): void {
    if (this.raceRoute?.content && isPlatformBrowser(this.platformId)) {
      import("../../../../../../components/leaflet-maps/gpx-layer").then(({GPX}) => {
        this.gpx = new GPX(this.raceRoute!.content, {}, () => {
        });
      });
    }
  }

  private initForm() {
    this.inputForm.controls.waypointTypeId.addValidators([required]);
    this.inputForm.controls.pointNameHr.addValidators([required]);
    if (this.disableEditing) {
      this.inputForm.disable();
    }
  }

  onSelectPoint(location: any) {
    this.setSelectedPointData(location);
  }

  private setSelectedPointData(location: any) {
    this.inputForm.patchValue({
      latitude: location.lat,
      longitude: location.lng,
      elevation: location.meta?.ele ? parseInt(location.meta.ele) : null,
      distanceFromStart: location.meta?.distanceFromStart ? parseInt(location.meta.distanceFromStart) : null
    });
    this.currentWaypoint = {...this.currentWaypoint, ...location, ...location.meta};
  }

  private subscribeToWaypointTypeChange() {
    this.subscriptions.push(this.inputForm.controls.waypointTypeId.valueChanges.subscribe((waypointTypeId: number) => {
      this.updateValidators(waypointTypeId)
    }));
  }

  togglePointSelection() {
    this.isPointSelectionEnabled = !this.isPointSelectionEnabled;
  }

  onSave(): void {
    if (!this.inputForm.valid || this.disableEditing || !this.race?.id) {
      this.toastService.showToast(TOAST_STATE.danger, this.translocoService.translate("organiser.race.details.save.error.no.raceId"));
      return;
    }
    const waypoint = this.getWaypointFields();
    if (!this._isWaypointValid(waypoint)) return;
    waypoint.raceId = this.race.id;
    waypoint.eventId = this.event?.id;
    this.loaderService.startLoading();
    this.waypointService.updateWaypoint(waypoint, () => {
      this.resetForm();
      this.waypointService.findWaypoints(this.race!.id).subscribe(list => {
        this.loaderService.dismissLoading();
        this.toastService.showToast(TOAST_STATE.success, this.translocoService.translate("organiser.event.details.control.point.save.success"));
        this.waypointList = list;
        this.waypointListChange.emit(list);
      });
    }, () => {
      this.loaderService.dismissLoading();
      this.toastService.showToast(TOAST_STATE.danger, this.translocoService.translate("organiser.event.details.control.point.save.error"));
    });
  }

  public resetForm() {
    this.currentWaypoint = new Waypoint();
    this.isPointSelectionEnabled = false;
    this.inputForm.reset();
    this.inputForm.controls.waypointTypeId.enable();
  }

  /**
   * Check that there isn't already a waypoint with same data in list of waypoints
   * @param waypointItem
   */
  _isWaypointValid(waypointItem: Waypoint): boolean {
    let isWaypointValid: boolean = true;
    if (!this.waypointList || this.waypointList.length === 0) {
      return isWaypointValid;
    }
    if (waypointItem.waypointTypeId === WaypointType.FINISH_WAYPOINT) {
      let waypoint: Waypoint | undefined = this.waypointList?.find((waypoint: Waypoint) => waypoint.waypointTypeId === WaypointType.FINISH_WAYPOINT);
      if (waypoint && waypoint.id !== waypointItem.id) {
        isWaypointValid = false;
        this.toastService.showToast(TOAST_STATE.danger,
          this.translocoService.translate("organiser.event.details.control.point.error.existingFinishWaypoint"));
      }
    } else if (waypointItem.waypointTypeId === WaypointType.START_WAYPOINT) {
      let waypoint: Waypoint | undefined = this.waypointList?.find((waypoint: Waypoint) => waypoint.waypointTypeId === WaypointType.START_WAYPOINT);
      if (waypoint && waypoint.id !== waypointItem.id) {
        isWaypointValid = false;
        this.toastService.showToast(TOAST_STATE.danger,
          this.translocoService.translate("organiser.event.details.control.point.error.existingStartWaypoint"));
      }
    } else if (waypointItem.waypointTypeId === WaypointType.CONTROL_WAYPOINT) {
      let waypoint: Waypoint | undefined = this.waypointList?.find((waypoint: Waypoint) => waypoint.waypointTypeId === WaypointType.CONTROL_WAYPOINT && waypoint.ordinalNumber === waypointItem.ordinalNumber);
      if (waypoint && waypoint.id !== waypointItem.id) {
        isWaypointValid = false;
        this.toastService.showToast(TOAST_STATE.danger,
          this.translocoService.translate("organiser.event.details.control.point.error.existingControlWaypoint"));
      }
    }
    return isWaypointValid;
  }

  onCancel() {
    this.resetForm();
  }

  hasFormAnyValue(): boolean {
    return !!this.currentWaypoint.id || this.inputForm.dirty;
  }

  onDeleteWaypoint(waypoint: Waypoint) {
    const modalRef = this.modalService.open(ConfirmationModalComponent, {centered: true});
    modalRef.componentInstance.title = this.translocoService.translate("organiser.event.details.control.point.delete.dialog.title");
    modalRef.componentInstance.text = this.translocoService.translate("organiser.event.details.control.point.delete.dialog.text");
    modalRef.result.then(() => this.deleteWaypoint(waypoint), () => {
    });
  }

  deleteWaypoint(waypoint: Waypoint): void {
    this.loaderService.startLoading();
    this.waypointService.deleteWaypoint(waypoint.id!, this.race!.id!, () => {
      this.resetForm();
      this.waypointService.findWaypoints(this.race!.id!).subscribe(list => {
        this.loaderService.dismissLoading();
        this.toastService.showToast(TOAST_STATE.success, this.translocoService.translate("organiser.event.details.control.point.delete.success"));
        this.waypointList = list;
        this.waypointListChange.emit(list);
      });
    }, () => {
      this.loaderService.dismissLoading();
      this.toastService.showToast(TOAST_STATE.danger, this.translocoService.translate("organiser.event.details.control.point.delete.error"));
    });
  }

  onEditWaypoint(waypoint: Waypoint): void {
    this.currentWaypoint = waypoint;
    this.inputForm.patchValue(waypoint);
    if (waypoint.waypointTypeId == WaypointType.START_WAYPOINT || waypoint.waypointTypeId == WaypointType.FINISH_WAYPOINT) {
      this.inputForm.controls.waypointTypeId.disable();
    }
    else {
      this.inputForm.controls.waypointTypeId.enable();
    }
    this.isPointSelectionEnabled = true;
    this.inputForm.controls.pointSelection.setValue(true);
    this.updateValidators(waypoint.waypointTypeId!);
  }

  private updateValidators(waypointTypeId: number) {
    let requiredFields: FormControl[] = [];
    let nonRequiredFields: FormControl[] = [];
    if (waypointTypeId === WaypointType.START_WAYPOINT || waypointTypeId === WaypointType.FINISH_WAYPOINT) {
      requiredFields = [];
    } else {
      nonRequiredFields = [this.inputForm.controls.city, this.inputForm.controls.address,
        this.inputForm.controls.additionalMark, this.inputForm.controls.country];
    }
    if (waypointTypeId === WaypointType.CONTROL_WAYPOINT) {
      requiredFields = [...requiredFields, this.inputForm.controls.ordinalNumber];
    } else {
      this.inputForm.controls.includedInRanking.setValue(undefined);
      nonRequiredFields = [...nonRequiredFields, this.inputForm.controls.ordinalNumber];
    }
    requiredFields.forEach(control => {
      control.addValidators([required]);
      control.updateValueAndValidity();
    });
    nonRequiredFields.forEach(control => {
      control.removeValidators([required]);
      control.updateValueAndValidity();
    });
  }

  onCopyWaypoint(waypoint: Waypoint) {
    const copyWaypoint = {
      ...waypoint,
      id: undefined,
      pointNameHr: undefined,
      pointNameEn: undefined,
      pointNameIt: undefined,
      pointNameSlo: undefined,
      pointNameSr: undefined,
      latitude: undefined,
      longitude: undefined
    };
    if (copyWaypoint.waypointTypeId === WaypointType.CONTROL_WAYPOINT) {
      copyWaypoint.ordinalNumber = undefined;
      copyWaypoint.includedInRanking = undefined;
    }
    this.onEditWaypoint(copyWaypoint);
  }

  getWaypointFields(): Waypoint {
    this.checkValidators(this.inputForm);
    return {
      ...this.currentWaypoint, ...this.inputForm.value,
      elevation: parseInt(this.inputForm.value.elevation),
      distanceFromStart: parseInt(this.inputForm.value.distanceFromStart),
      ordinalNumber: this.inputForm.value.ordinalNumber ? parseInt(this.inputForm.value.ordinalNumber) : undefined
    };
  }

  processFile(fileInput: any): void {
    if (!this.race?.id) {
      this.toastService.showToast(TOAST_STATE.danger, this.translocoService.translate("organiser.race.details.save.error.no.raceId"));
      return;
    }
    const file: File = fileInput.files[0];
    if (file.size > environment.maxFileSize) {
      this.toastService.showToast(TOAST_STATE.danger, this.translocoService.translate("app.user.profile.max.size.error"));
      return;
    }
    const reader = new FileReader();
    reader.addEventListener('load', (event: any) => {
      this.loaderService.startLoading();
      this.raceRouteService.updateRaceRoute(new RaceRoute(this.race!.id!, undefined, event.target.result), () => {
        this.raceRouteService.fetchRaceRoute(this.race!.id!).subscribe(raceRoute => {
          this.loaderService.dismissLoading();
          this.toastService.showToast(TOAST_STATE.success, this.translocoService.translate("organiser.event.details.control.point.race.route.save.success"));
          raceRoute!.content = event.target.result;
          this.raceRoute = raceRoute;
          this.raceRouteChange.emit(raceRoute);
          this.parseGpx();
        });
      }, () => {
        this.loaderService.dismissLoading();
        this.toastService.showToast(TOAST_STATE.danger, this.translocoService.translate("organiser.event.details.control.point.race.route.save.error"));
      });
    });
    reader.readAsDataURL(file);
  }

  onDeleteGpxFile(): void {
    this.fileUploadInputVariable!.nativeElement.value = "";
    this.loaderService.startLoading();
    this.raceRouteService.deleteRaceRoute(this.race!.id!, () => {
      this.loaderService.dismissLoading();
      this.resetForm();
      this.raceRoute = undefined;
      this.raceRouteChange.emit(undefined);
      this.gpx = undefined;
      this.toastService.showToast(TOAST_STATE.success, this.translocoService.translate("organiser.event.details.control.point.race.route.delete.success"));
    }, () => {
      this.loaderService.dismissLoading();
      this.toastService.showToast(TOAST_STATE.danger, this.translocoService.translate("organiser.event.details.control.point.race.route.delete.error"));
    });
  }

  getNameColumn() {
    return `pointName${this.translocoService.getActiveLang().toUpperCase()}`;
  }

  onCopyStartFromMap() {
    const location = this.gpx?.getCoords()?.[0];
    if (location) this.setSelectedPointData(location);
  }

  onCopyEndFromMap() {
    const location = this.gpx?.getCoords()?.[this.gpx.getCoords().length - 1];
    if (location) this.setSelectedPointData(location);
  }

  getOptions(): Map<number, string> {
    return new Map(this.waypointMarkerTypes.map(item => [item.id!, this.translocoService.translate('app.common.waypoint.marker.types.' + item.id)]));
  }

  openSelectWaypointFromEventModal() {
    this.loaderService.startLoading();
    this.waypointService.findEventWaypoints(this.event!.id!).pipe(finalize(() => this.loaderService.dismissLoading())
    ).subscribe(waypoints => {
      this.loaderService.dismissLoading();
      const modalRef = this.modalService.open(SelectWaypointFromEventModalComponent, {
        size: 'lg',
        scrollable: true, centered: true
      });
      modalRef.componentInstance.raceWaypointsComponent = this;
      modalRef.componentInstance.listOfWaypoints = waypoints;
      modalRef.result.then((selectedWaypoint: Waypoint) => {
        if (selectedWaypoint) {
          this.onEditWaypoint(selectedWaypoint);
        }
      }, () => {
      });
    });
  }

  protected readonly Waypoint = Waypoint;
  protected readonly WaypointType = WaypointType;
}
